import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";

// Lote 137 (Robson, 28/10/2026) — quadro de avisos do Painel, logo abaixo dos atalhos: "Pendências" (vermelho) e
// "Revisão" (azul) se revezam no mesmo espaço, 1 minuto cada. O letreiro só avisa que eles existem. Tocar no
// quadro abre a respectiva janela (1 toque). Com só um dos dois, ele fica fixo; sem nenhum, o quadro não aparece.
// Passar o mouse por cima pausa o revezamento; os dois pontinhos trocam na hora (celular e computador).
export const AVISOS_INTERVALO_MS = 60000;

const TOM = {
  red: { box: "bg-red-50 border-red-300 text-red-800", dot: "bg-red-600", ponto: "bg-red-600", btn: "text-red-800" },
  blue: { box: "bg-blue-50 border-blue-300 text-blue-800", dot: "bg-blue-600", ponto: "bg-blue-600", btn: "text-blue-800" },
};

export default function AvisosPainel({ pend, rev, onOpen, intervalMs = AVISOS_INTERVALO_MS }) {
  const itens = [
    pend?.total > 0 ? { key: "pend", tone: "red", titulo: `${pend.total} pendência${pend.total > 1 ? "s" : ""}`, detalhe: pend.partes } : null,
    rev?.total > 0 ? { key: "rev", tone: "blue", titulo: (rev.total > 1 ? `${rev.total} revisões` : "1 revisão"), detalhe: rev.partes } : null,
  ].filter(Boolean);
  const chaves = itens.map((i) => i.key).join("|");
  const [idx, setIdx] = useState(0);
  const [pausado, setPausado] = useState(false);

  // Mudou o que existe (ex.: a última pendência foi resolvida): volta ao primeiro.
  useEffect(() => { setIdx(0); }, [chaves]);

  useEffect(() => {
    if (itens.length < 2 || pausado) return undefined;
    const t = setTimeout(() => setIdx((i) => (i + 1) % itens.length), intervalMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaves, idx, pausado, intervalMs]);

  if (itens.length === 0) return null;
  const cur = itens[idx % itens.length];
  const tom = TOM[cur.tone];
  return (
    <div
      id="painel-avisos"
      data-testid="painel-avisos"
      data-aviso={cur.key}
      data-total={itens.length}
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      className="scroll-mt-24"
    >
      <button
        key={cur.key}
        type="button"
        data-testid={`painel-aviso-${cur.key}`}
        onClick={() => onOpen(cur.key)}
        className={`w-full flex items-center gap-3 text-left rounded-lg border px-3.5 min-h-[56px] py-2.5 motion-safe:animate-in motion-safe:fade-in duration-500 ${tom.box}`}
      >
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 motion-safe:animate-pulse ${tom.dot}`} style={{ animationDuration: "1s" }} />
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold leading-snug">{cur.titulo}</span>
          {cur.detalhe && <span className="block text-sm opacity-85 leading-snug break-words">{cur.detalhe}</span>}
        </span>
        <span className={`shrink-0 inline-flex items-center gap-0.5 text-sm font-semibold ${tom.btn}`}>
          Abrir <ChevronRight className="w-4 h-4" strokeWidth={2.25} />
        </span>
      </button>
      {itens.length > 1 && (
        <div className="flex justify-center gap-1 mt-0.5" data-testid="painel-avisos-pontos">
          {itens.map((it, i) => (
            <button
              key={it.key}
              type="button"
              data-testid={`painel-avisos-ponto-${it.key}`}
              aria-label={it.key === "pend" ? "Mostrar pendências" : "Mostrar revisão"}
              aria-current={i === idx % itens.length}
              onClick={() => setIdx(i)}
              className="w-6 h-6 inline-flex items-center justify-center"
            >
              <span className={`block rounded-full transition-all ${TOM[it.tone].ponto} ${i === idx % itens.length ? "w-2.5 h-2.5" : "w-2 h-2 opacity-35"}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
